import { cn } from '@/lib/utils';
import { MapPin, Calendar, Clock, Users } from 'lucide-react';
import { sanitizeRichTextHtml } from '@/lib/richtext/sanitizeRichTextHtml';
import { formatEventTimingSummary } from '@/lib/events/eventCardUtils';

/**
 * ActivityPagePreview — page-like, non-interactive preview that mirrors
 * the public activity/event page. Used in the editor's main (left) column.
 *
 * Follows the preview-first editorial standard:
 *   Hero → Title → Date/meta → Description → CTA (disabled)
 *
 * Driven entirely by local form state — no API calls.
 */

interface ActivityPagePreviewProps {
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  eventDate?: string | null;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  timingMode?: string | null;
  selectedSeasons?: string[] | null;
  isFree: boolean;
  priceSek?: number | null;
  locationText?: string | null;
  bookingMode?: string;
  eventType?: string;
  maxSpots?: number | null;
}

export function ActivityPagePreview({
  title,
  description,
  coverImageUrl,
  eventDate,
  endDate,
  startTime,
  endTime,
  timingMode,
  selectedSeasons,
  isFree,
  priceSek,
  locationText,
  bookingMode = 'none',
  eventType = 'event',
  maxSpots,
}: ActivityPagePreviewProps) {
  const hasHero = !!coverImageUrl;
  const priceLabel = isFree ? 'Gratis' : (priceSek && priceSek > 0 ? `${priceSek} kr/person` : null);

  const previewStartDate = timingMode === 'date_time' && eventDate
    ? `${eventDate}T${startTime || '00:00'}:00`
    : eventDate;
  const previewEndDate = timingMode === 'date_time' && eventDate
    ? `${eventDate}T${endTime || startTime || '00:00'}:00`
    : endDate;
  const seasonTags = (selectedSeasons || []).map((season) => `season:${season}`);
  const dateLabel = formatEventTimingSummary(
    (timingMode as any) || 'date_time',
    previewStartDate,
    previewEndDate,
    seasonTags,
  );

  const ctaLabel = bookingMode === 'required'
    ? 'Boka plats'
    : bookingMode === 'optional'
      ? 'Visa intresse'
      : null;

  // Sanitize HTML description for safe rendering
  const cleanDescription = description ? sanitizeRichTextHtml(description) : null;

  return (
    <div className="rounded-2xl border border-border/30 bg-card shadow-sm overflow-hidden">
      {/* Hero — real image or collapse entirely */}
      {hasHero && (
        <div className="relative aspect-[3/2] overflow-hidden">
          <img
            src={coverImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
          {/* Soft gradient at bottom for text legibility */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-card/80 to-transparent" />
        </div>
      )}

      {/* Content area */}
      <div className={cn('px-8 pb-8 max-w-2xl', hasHero ? 'pt-5' : 'pt-8')}>
        {/* Type badge */}
        {eventType === 'course' && (
          <span className="inline-block text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60 mb-2">
            Kurs
          </span>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground leading-tight tracking-tight">
          {title || (
            <span className="text-muted-foreground/40 italic font-normal text-xl">
              Namnlös aktivitet
            </span>
          )}
        </h1>

        {/* Meta row — date, location, price */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-muted-foreground">
          {dateLabel && (
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              {dateLabel}
            </span>
          )}
          {locationText && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {locationText.length > 30 ? locationText.slice(0, 27) + '…' : locationText}
            </span>
          )}
          {priceLabel && (
            <span className="font-medium text-foreground/80">{priceLabel}</span>
          )}
        </div>

        {/* Capacity hint */}
        {maxSpots && bookingMode !== 'none' && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground/60">
            <Users className="h-3 w-3" />
            <span>Max {maxSpots} deltagare</span>
          </div>
        )}

        {/* Description */}
        {cleanDescription ? (
          <div
            className="mt-6 prose prose-sm prose-muted max-w-none text-foreground/80 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: cleanDescription }}
          />
        ) : (
          <p className="mt-6 text-sm text-muted-foreground/40 italic leading-relaxed">
            Beskriv vad som händer och vad gästerna kan förvänta sig…
          </p>
        )}

        {/* CTA button (disabled, non-interactive) */}
        {ctaLabel && (
          <div className="mt-8">
            <button
              disabled
              className="inline-flex items-center justify-center rounded-lg bg-primary/80 text-primary-foreground px-6 py-2.5 text-sm font-medium opacity-60 cursor-default"
            >
              {ctaLabel}
            </button>
            <p className="text-[10px] text-muted-foreground/40 mt-1.5">
              Förhandsvisning — knappen är inte aktiv
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
